import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, AlertCircle } from "lucide-react";
import { trackPlayVideo } from "@/lib/analytics";

/**
 * VideoModal — accessible dialog wrapper around a native <video>.
 *
 * Behaviour:
 *  - Never autoplays. User must press the native play control.
 *  - ESC key closes (Radix Dialog default).
 *  - Backdrop click closes (Radix Dialog default).
 *  - Focus is trapped inside the dialog while open.
 *  - Body scroll is locked (Radix default) → page scroll preserved on close.
 *  - On close: React unmounts the <video> entirely (below), which aborts any
 *    in-flight fetch and releases audio — no manual cleanup needed.
 *
 * Implementation note:
 *  Historically we used <source> children under <video>. When mounted inside
 *  a Radix Dialog portal, some browsers failed to trigger the resource
 *  selection algorithm on the source children, leaving networkState=0 and
 *  readyState=0 (the film would never load, even though the poster showed).
 *  We now pick the best supported source *before* mount using canPlayType and
 *  set it directly as the `src` attribute — this is more reliable and
 *  avoids any dependency on browser source-selection quirks.
 */
export function VideoModal({ open, onOpenChange, title, description, poster, posterAlt, sources, videoId, placement }) {
    const videoRef = useRef(null);
    const [loadError, setLoadError] = useState(null);
    // Guard against multiple play events firing (pause/resume) within one open.
    const playFiredRef = useRef(false);

    // Pick the best source the browser can actually play. Prefer the caller's
    // stated order (WebM first is typical), but fall back to any playable one.
    const chosenSrc = useMemo(() => {
        if (!open) return null;
        if (typeof document === "undefined") return null;
        const probe = document.createElement("video");
        const list = Array.isArray(sources) ? sources : [];
        for (const s of list) {
            if (!s || !s.src) continue;
            const canPlay = probe.canPlayType(s.type || "");
            // canPlayType returns "probably", "maybe", or "" (empty = no)
            if (canPlay === "probably" || canPlay === "maybe") {
                return s.src;
            }
        }
        // Fallback: nothing matched, pick the first entry anyway so the browser
        // can attempt playback (better than nothing).
        return list.length ? list[0].src : null;
    }, [open, sources]);

    useEffect(() => {
        if (!open) {
            setLoadError(null);
            playFiredRef.current = false;
            return;
        }
        // After mount, explicitly call load() so the browser starts fetching
        // metadata immediately. This is a belt-and-braces call — the src
        // attribute should trigger loading on its own, but load() is safe to
        // call and eliminates any edge cases where the initial paint completes
        // before the resource selection algorithm has kicked in.
        const raf = requestAnimationFrame(() => {
            const v = videoRef.current;
            if (v) {
                try {
                    v.load();
                } catch (_e) {
                    // no-op — error event will surface any genuine failure
                }
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [open, chosenSrc]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="video-modal"
                className="p-0 border-white/10 bg-[#07070A] w-[min(96vw,1200px)] max-w-none sm:rounded-2xl overflow-hidden [&>button:last-of-type]:hidden"
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                }}
            >
                <DialogTitle className="sr-only">{title}</DialogTitle>
                {description ? (
                    <DialogDescription className="sr-only">{description}</DialogDescription>
                ) : null}

                <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    aria-label="Close video"
                    data-testid="video-modal-close"
                    className="absolute right-3 top-3 z-20 h-10 w-10 rounded-full border border-white/20 bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-colors"
                >
                    <X size={18} />
                </button>

                {open && chosenSrc ? (
                    <div className="relative w-full bg-black" style={{ aspectRatio: "16 / 9" }}>
                        <video
                            key={chosenSrc}
                            ref={videoRef}
                            src={chosenSrc}
                            controls
                            playsInline
                            preload="metadata"
                            poster={poster}
                            className="w-full h-full bg-black"
                            data-testid="video-modal-player"
                            aria-label={posterAlt || title}
                            onPlay={() => {
                                if (playFiredRef.current) return;
                                playFiredRef.current = true;
                                trackPlayVideo({
                                    video_id: videoId || title || "unknown",
                                    placement: placement || "video_modal",
                                    source: "video_modal",
                                });
                            }}
                            onError={() => {
                                const v = videoRef.current;
                                const code = v && v.error ? v.error.code : null;
                                const msg = v && v.error ? v.error.message : "Unknown video error.";
                                setLoadError({ code, msg });
                                console.warn("[VideoModal] video error", code, msg, "src=", chosenSrc);
                            }}
                        >
                            Sorry, your browser does not support embedded video.
                        </video>

                        {loadError ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/85 p-6 text-center">
                                <div className="max-w-md">
                                    <div className="mx-auto mb-4 h-10 w-10 rounded-full border border-white/20 flex items-center justify-center text-white/80">
                                        <AlertCircle size={18} />
                                    </div>
                                    <div className="font-display text-xl text-white mb-2">
                                        Film unavailable right now
                                    </div>
                                    <p className="text-sm text-white/70">
                                        We couldn&rsquo;t start the video. Please try again in a moment or reach out to the Pulse team.
                                    </p>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

export default VideoModal;
